import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { LayoutDashboard, Globe } from 'lucide-react';
import { triggerBrowserDownload, trackDirectDownload } from '../../../utils/downloadResume';
import { apiUrl } from '../../../utils/apiBase';
import { defaultLogoCdnUrl, legacyApiPath, CANDOR_WEB_IDE_URL } from '../../../config/environments';
import { detectUserEnvironmentSync } from '../../../utils/detectEnvironment';

// OS Icons
function WindowsIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 88 88" fill="currentColor">
      <path d="M0 12.5L35.7 7.6V41.7H0V12.5ZM0 46.3H35.7V80.4L0 75.5V46.3ZM40.1 7.1L87.8 0V41.7H40.1V7.1ZM40.1 46.3H87.8V88L40.1 80.9V46.3Z" />
    </svg>
  );
}

function AppleIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 170 170" fill="currentColor">
      <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.07-7.73-7.94-12.1-14.61-6.19-9.5-11.05-20.3-14.58-32.39-3.53-12.1-5.3-23.73-5.3-34.9 0-14.07 3.52-25.95 10.57-35.63 7.05-9.69 16.03-14.62 26.94-14.8 5.17 0 10.98 1.48 17.43 4.43 6.45 2.96 10.74 4.49 12.87 4.59 1.74 0 6.26-1.63 13.57-4.88 7.3-3.26 13.43-4.63 18.38-4.12 14.05 1.07 24.89 6.27 32.53 15.61-12.61 7.64-18.75 18.06-18.42 31.27.32 10.42 4.26 19.16 11.82 26.23 7.56 7.06 16.59 11.25 27.09 12.56-2.5 7.62-5.65 15.22-9.45 22.81zM119.22 33.15c0-7.39 2.65-14.54 7.94-21.46 5.3-6.92 11.85-11.23 19.66-12.94 1.09 7.72-1.09 15.02-6.53 21.91-5.44 6.89-12.21 11.19-20.32 12.89-.11-.13-.75-.4-.75-.4z" />
    </svg>
  );
}

function LinuxIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2a4 4 0 0 0-4 4v3.5a1.5 1.5 0 0 0 .5 1.14c.32.28.5.69.5 1.11 0 1.93-1.57 3.5-3.5 3.5H5a2 2 0 0 0-2 2c0 .55.45 1 1 1h.5a1.5 1.5 0 0 0 1.5-1.5c0-.28.22-.5.5-.5h.5c.28 0 .5.22.5.5A2.5 2.5 0 0 0 9 19h6a2.5 2.5 0 0 0 2-1.5c0-.28.22-.5.5-.5h.5c.28 0 .5.22.5.5A1.5 1.5 0 0 0 19.5 19H20a1 1 0 0 0 1-1 2 2 0 0 0-2-2h-.5c-1.93 0-3.5-1.57-3.5-3.5 0-.42.18-.83.5-1.11A1.5 1.5 0 0 0 16 9.5V6a4 4 0 0 0-4-4zm-2 5a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm4 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm-3 2h2a1 1 0 0 1 1 1c0 .55-.45 1-1 1h-2a1 1 0 0 1-1-1c0-.55.45-1 1-1z" />
    </svg>
  );
}

function renderOsIcon(os, className = 'w-4 h-4') {
  if (os === 'macos') return <AppleIcon className={className} />;
  if (os === 'linux') return <LinuxIcon className={className} />;
  return <WindowsIcon className={className} />;
}

export default function Navbar({ onOpenContact, onOpenDownload, onOpenLogin }) {
  const { isAuthenticated, logout, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);
  const [tryNowDropdownOpen, setTryNowDropdownOpen] = useState(false);
  const [detectedEnv, setDetectedEnv] = useState(() => detectUserEnvironmentSync());
  const [manifestData, setManifestData] = useState(null);
  const [manifestVersion, setManifestVersion] = useState('1.0.4');
  const tryNowRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // High-entropy Client Hints for Chromium browsers (Chrome, Edge, Brave, etc.)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.navigator?.userAgentData?.getHighEntropyValues) {
      window.navigator.userAgentData
        .getHighEntropyValues(['architecture', 'bitness', 'platform', 'platformVersion'])
        .then((hints) => {
          const rawPlatform = (hints.platform || window.navigator.userAgentData?.platform || '').toLowerCase();
          const isArm = hints.architecture === 'arm';
          const arch = isArm ? 'ARM64' : (hints.bitness === '32' ? 'x86' : 'x64');

          setDetectedEnv((prev) => {
            let os = prev.os;
            let name = prev.name;
            let detail = prev.detail;

            if (rawPlatform.includes('win')) {
              os = 'windows';
              name = 'Windows';
              const majorVersion = parseInt(hints.platformVersion?.split('.')[0] || '0', 10);
              const winName = majorVersion >= 13 ? 'Windows 11' : 'Windows 10';
              detail = isArm ? `${winName} on ARM` : `${winName} (${arch})`;
            } else if (rawPlatform.includes('mac')) {
              os = 'macos';
              name = 'macOS';
              detail = isArm ? 'macOS (Apple Silicon M-Series)' : 'macOS (Intel 64-bit)';
            } else if (rawPlatform.includes('lin')) {
              os = 'linux';
              name = 'Linux';
              detail = `Linux (${arch})`;
            }

            return {
              ...prev,
              os,
              name,
              arch,
              detail,
              isAutoDetected: true,
            };
          });
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    fetch('/update_manifest.json')
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Failed to load');
      })
      .then(data => {
        if (data) {
          setManifestData(data);
          if (data.version) {
            setManifestVersion(data.version);
          }
        }
      })
      .catch(err => {
        console.warn('Could not fetch update_manifest.json:', err);
      });
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (tryNowRef.current && !tryNowRef.current.contains(event.target)) {
        setTryNowDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  // Resolve platform and latest build for detected OS
  const version = manifestVersion || manifestData?.version || '1.0.4';

  const platforms = manifestData?.platforms || {
    windows: {
      name: 'Windows',
      architectures: [
        {
          id: 'win-exe',
          name: 'Windows Installer (.exe)',
          arch: 'x64',
          format: '.exe',
          downloadUrl: `/candor-exe/CandorSI-Setup-v${version}.exe`,
          fallbackUrl: `/candor-exe/CandorSI-Setup-v${version}.zip`,
          size: '~403 MB',
          badge: 'Recommended (.exe)',
          status: 'available',
        },
        {
          id: 'win-zip',
          name: 'Portable Archive (.zip)',
          arch: 'x64',
          format: '.zip',
          downloadUrl: `/candor-exe/CandorSI-Setup-v${version}.zip`,
          size: '~402 MB',
          badge: 'Portable ZIP',
          status: 'available',
        },
      ],
    },
    macos: {
      name: 'macOS',
      architectures: [
        {
          id: 'mac-arm64',
          name: 'Apple Silicon (M-Series)',
          arch: 'ARM64',
          format: '.dmg',
          downloadUrl: `/candor-exe/CandorSI-mac-arm64-v${version}.dmg`,
          fallbackUrl: `/candor-exe/CandorSI-v${version}.vsix`,
          size: '~152 MB',
          badge: 'Coming Soon',
          status: 'coming_soon',
        },
        {
          id: 'mac-intel',
          name: 'Intel Mac (64-bit)',
          arch: 'x64',
          format: '.dmg',
          downloadUrl: `/candor-exe/CandorSI-mac-x64-v${version}.dmg`,
          fallbackUrl: `/candor-exe/CandorSI-v${version}.vsix`,
          size: '~155 MB',
          badge: 'Coming Soon',
          status: 'coming_soon',
        },
      ],
    },
    linux: {
      name: 'Linux',
      architectures: [
        {
          id: 'linux-deb',
          name: 'Debian / Ubuntu Package (.deb)',
          arch: 'x64',
          format: '.deb',
          downloadUrl: `/candor-exe/CandorSI-v${version}.deb`,
          fallbackUrl: `/candor-exe/CandorSI-v${version}.tar.gz`,
          size: '~497 MB',
          badge: 'Official .deb',
          status: 'available',
        },
        {
          id: 'linux-tar',
          name: 'Generic Linux Archive (.tar.gz)',
          arch: 'x64',
          format: '.tar.gz',
          downloadUrl: `/candor-exe/CandorSI-v${version}.tar.gz`,
          fallbackUrl: `/candor-exe/CandorSI-v${version}.deb`,
          size: '~497 MB',
          badge: 'Tarball',
          status: 'available',
        },
      ],
    },
  };

  const userOsKey = detectedEnv.os || 'windows';
  const currentPlatform = platforms[userOsKey] || platforms.windows;
  const currentArchs = currentPlatform.architectures || [];

  // Pick primary build based on detected architecture
  const primaryBuild =
    currentArchs.find((a) => {
      if (a.status === 'coming_soon') return false;
      if (userOsKey === 'windows') return a.format === '.exe' || a.id.includes('exe');
      if (userOsKey === 'macos') {
        return detectedEnv.arch === 'ARM64' ? a.id.includes('arm64') : a.id.includes('intel');
      }
      if (userOsKey === 'linux') return a.format === '.deb' || a.id.includes('deb');
      return true;
    }) || currentArchs.find((a) => a.status !== 'coming_soon') || currentArchs[0];

  // Secondary builds for this OS
  const secondaryBuilds = currentArchs.filter((a) => a.id !== primaryBuild?.id);

  const handleDownloadBuild = (build) => {
    if (!build) return;
    const downloadUrl = build.downloadUrl || build.fallbackUrl || `/candor-exe/CandorSI-Setup-v${version}.zip`;
    if (isAuthenticated) {
      triggerBrowserDownload(downloadUrl);
      toast.success(`Your Candor IDE v${version} (${build.name || detectedEnv.name}) download has started!`);
      trackDirectDownload(user, downloadUrl, 'header_nav_try_now');
    } else {
      sessionStorage.setItem('candor_pending_download', JSON.stringify({ url: downloadUrl }));
      onOpenDownload();
    }
  };

  const handleSelectVersion = async (typeOrVersion) => {
    if (typeof typeOrVersion === 'object' && typeOrVersion !== null) {
      handleDownloadBuild(typeOrVersion);
    } else if (typeOrVersion === 'previous') {
      handleDownloadBuild(primaryBuild);
    } else if (typeOrVersion === 'latest') {
      if (isAuthenticated) {
        const payload = {
          first_name: user?.first_name || user?.name?.split(' ')[0] || 'User',
          last_name: user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '',
          title: user?.title || 'User',
          company_name: user?.company || 'N/A',
          email: user?.email || '',
          phone: user?.phone || '',
          subject: 'Request Access to Preview Version',
          message: `User ${user?.first_name || ''} ${user?.last_name || ''} (${user?.email || ''}) from ${user?.company || 'N/A'} is requesting access to the preview version of Candor SI.`
        };
        try {
          const res = await fetch(apiUrl(process.env.REACT_APP_ENDPOINT_AUTH_CONTACT_US || legacyApiPath('contact_us')), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (!res.ok) {
            throw new Error('Failed to submit contact request');
          }
          toast.success('Your request for the preview version has been sent successfully!');
        } catch (err) {
          toast.error(err.message || 'Failed to submit request. Please try again.');
        }
      } else {
        const subject = 'Request Access to Preview Version';
        const message = 'I would like to request access to the preview version of Candor SI. Please send me the download details.';
        onOpenContact(subject, message);
      }
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '';
    }
    return location.pathname === path;
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.05)] transition-all duration-300">
        <div className="max-w-[1440px] mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-16 xl:h-20">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/candor-brand-lockup.png`}
                  alt="Candor — Vibe Coding for Data Platform"
                  className="h-9 xl:h-11 w-auto object-contain max-w-[140px] xl:max-w-[165px]"
                  onError={(e) => {
                    e.target.src = `${process.env.PUBLIC_URL || ''}/candorlogo.png`;
                    e.target.onerror = () => { e.target.src = defaultLogoCdnUrl(); };
                  }}
                />
              </Link>
            </div>

            {/* Desktop Navigation Links */}
            <nav className="hidden lg:flex items-center space-x-0.5 xl:space-x-1">
              <Link
                to="/"
                className={`px-2.5 xl:px-3 py-1.5 rounded-full text-[13px] xl:text-[14px] font-semibold transition-all duration-200 whitespace-nowrap ${
                  isActive('/') ? 'text-[#1D4ED8] bg-blue-50' : 'text-gray-600 hover:text-[#1D4ED8] hover:bg-gray-50'
                }`}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`px-2.5 xl:px-3 py-1.5 rounded-full text-[13px] xl:text-[14px] font-semibold transition-all duration-200 whitespace-nowrap ${
                  isActive('/about') ? 'text-[#1D4ED8] bg-blue-50' : 'text-gray-600 hover:text-[#1D4ED8] hover:bg-gray-50'
                }`}
              >
                About
              </Link>
              <Link
                to="/features"
                className={`px-2.5 xl:px-3 py-1.5 rounded-full text-[13px] xl:text-[14px] font-semibold transition-all duration-200 whitespace-nowrap ${
                  isActive('/features') ? 'text-[#1D4ED8] bg-blue-50' : 'text-gray-600 hover:text-[#1D4ED8] hover:bg-gray-50'
                }`}
              >
                Features
              </Link>
              <Link
                to="/pricing"
                className={`px-2.5 xl:px-3 py-1.5 rounded-full text-[13px] xl:text-[14px] font-semibold transition-all duration-200 whitespace-nowrap ${
                  isActive('/pricing') ? 'text-[#1D4ED8] bg-blue-50' : 'text-gray-600 hover:text-[#1D4ED8] hover:bg-gray-50'
                }`}
              >
                Pricing
              </Link>
              <Link
                to="/download"
                className={`px-2.5 xl:px-3 py-1.5 rounded-full text-[13px] xl:text-[14px] font-semibold transition-all duration-200 whitespace-nowrap ${
                  isActive('/download') ? 'text-[#1D4ED8] bg-blue-50' : 'text-gray-600 hover:text-[#1D4ED8] hover:bg-gray-50'
                }`}
              >
                Download
              </Link>

              {/* Resources Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setResourcesDropdownOpen(true)}
                onMouseLeave={() => setResourcesDropdownOpen(false)}
              >
                <button
                  aria-expanded={resourcesDropdownOpen}
                  aria-haspopup="true"
                  aria-label="Toggle Resources Menu"
                  onClick={() => setResourcesDropdownOpen(!resourcesDropdownOpen)}
                  className={`px-2.5 xl:px-3 py-1.5 rounded-full text-[13px] xl:text-[14px] font-semibold transition-all duration-200 inline-flex items-center whitespace-nowrap focus-visible:ring-2 focus-visible:ring-[#1D4ED8] ${
                    resourcesDropdownOpen || isActive('/blog') || isActive('/newsletter') || isActive('/press-release') || isActive('/security')
                      ? 'text-[#1D4ED8] bg-blue-50'
                      : 'text-gray-600 hover:text-[#1D4ED8] hover:bg-gray-50'
                  }`}
                >
                  Resources
                  <svg
                    className={`ml-1 h-3.5 w-3.5 transition-transform duration-200 ${
                      resourcesDropdownOpen ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {resourcesDropdownOpen && (
                  <div role="menu" className="absolute left-1/2 transform -translate-x-1/2 w-48 rounded-xl bg-white shadow-xl border border-slate-100 py-2 mt-0 transition-all duration-200 z-50">
                    <Link
                      to="/blog"
                      role="menuitem"
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50/50 hover:text-blue-600"
                    >
                      Blog
                    </Link>
                    <Link
                      to="/newsletter"
                      role="menuitem"
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50/50 hover:text-blue-600"
                    >
                      Newsletter
                    </Link>
                    <Link
                      to="/press-release"
                      role="menuitem"
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50/50 hover:text-blue-600"
                    >
                      Press Release
                    </Link>
                    <Link
                      to="/security"
                      role="menuitem"
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50/50 hover:text-blue-600"
                    >
                      Security
                    </Link>
                  </div>
                )}
              </div>
            </nav>

            {/* CTAs */}
            <div className="hidden lg:flex items-center space-x-1 xl:space-x-1.5">
              <button
                onClick={onOpenContact}
                aria-label="Open Contact Us modal"
                className="px-2.5 xl:px-3 py-1 rounded-full text-[12px] xl:text-[12.5px] font-semibold text-gray-700 hover:text-[#1D4ED8] hover:bg-gray-50 transition-colors border border-gray-200 focus-visible:ring-2 focus-visible:ring-[#1D4ED8] whitespace-nowrap"
              >
                Contact Us
              </button>

              {/* Try Now Dropdown - Auto-detects OS & displays only latest version */}
              <div className="relative" ref={tryNowRef}>
                <button
                  onClick={() => setTryNowDropdownOpen(!tryNowDropdownOpen)}
                  aria-expanded={tryNowDropdownOpen}
                  aria-haspopup="true"
                  aria-label="Toggle Try Now options dropdown"
                  className="px-2.5 xl:px-3 py-1 rounded-full text-[12px] xl:text-[12.5px] font-bold text-white bg-[#1D4ED8] hover:bg-blue-800 shadow-sm transition-all hover:-translate-y-0.5 inline-flex items-center gap-1 xl:gap-1.5 focus-visible:ring-2 focus-visible:ring-[#1D4ED8] whitespace-nowrap"
                >
                  {renderOsIcon(detectedEnv.os, "w-3 h-3 xl:w-3.5 xl:h-3.5 text-white")}
                  <span>Try Now Candor 2.0</span>
                  <svg
                    className={`h-3 w-3 xl:h-3.5 xl:w-3.5 transition-transform duration-200 ${
                      tryNowDropdownOpen ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {tryNowDropdownOpen && (
                  <div className="absolute right-0 w-80 rounded-2xl bg-white shadow-2xl border border-slate-100 py-2.5 mt-2 transition-all duration-200 z-50 text-left">
                    {/* Auto-detected OS status header */}
                    <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/80 rounded-t-xl flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                        <span>Detected: {detectedEnv.detail || detectedEnv.name}</span>
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                        v{manifestVersion}
                      </span>
                    </div>

                    {/* Primary build for detected OS */}
                    <div className="p-2 space-y-1">
                      <button
                        onClick={() => {
                          setTryNowDropdownOpen(false);
                          handleDownloadBuild(primaryBuild);
                        }}
                        className="w-full text-left p-3 rounded-xl bg-blue-50/80 hover:bg-blue-100 border border-blue-200/80 transition-all group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 font-bold text-sm text-blue-900 group-hover:text-blue-700">
                            {renderOsIcon(detectedEnv.os, "w-4 h-4 text-blue-600")}
                            <span>Download for {detectedEnv.name}</span>
                          </div>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-600 text-white">
                            {primaryBuild.format || 'Latest'}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
                          <span className="font-medium text-slate-600">{primaryBuild.name} (v{manifestVersion})</span>
                        </div>
                      </button>

                      {/* Secondary builds for this detected OS */}
                      {secondaryBuilds.map((b) => {
                        const isComingSoon = b.status === 'coming_soon';
                        return (
                          <button
                            key={b.id || b.name}
                            disabled={isComingSoon}
                            onClick={() => {
                              if (isComingSoon) return;
                              setTryNowDropdownOpen(false);
                              handleDownloadBuild(b);
                            }}
                            className={`w-full text-left px-3 py-2.5 rounded-xl text-xs transition-colors flex items-center justify-between ${
                              isComingSoon
                                ? 'opacity-60 cursor-not-allowed bg-slate-50 text-slate-400'
                                : 'text-slate-700 hover:bg-slate-50 hover:text-blue-600'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{b.name}</span>
                              <span className="text-slate-400">({b.format || `v${manifestVersion}`})</span>
                            </div>
                            {isComingSoon && (
                              <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-200 text-slate-600">
                                Coming Soon
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>



                    {/* All platforms link */}
                    <div className="px-2 pt-1 border-t border-slate-100">
                      <Link
                        to="/download"
                        onClick={() => setTryNowDropdownOpen(false)}
                        className="block w-full text-left px-3 py-2 rounded-lg text-xs font-bold text-blue-600 hover:bg-blue-50/70 hover:text-blue-700 transition-colors"
                      >
                        All Platforms &amp; Builds (Win, Mac, Linux) →
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Candor Web IDE Button */}
              <a
                href={CANDOR_WEB_IDE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open Candor Web IDE"
                className="px-2.5 xl:px-3 py-1 rounded-full text-[12px] xl:text-[12.5px] font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm transition-all hover:-translate-y-0.5 inline-flex items-center gap-1 xl:gap-1.5 focus-visible:ring-2 focus-visible:ring-indigo-500 whitespace-nowrap"
              >
                <Globe className="w-3 h-3 xl:w-3.5 xl:h-3.5 text-blue-100" />
                <span>Candor Web IDE</span>
              </a>

              {/* Download & Auth Actions */}
              {isAuthenticated ? (
                <>
                  <Link
                    to="/download"
                    className="px-2.5 xl:px-3 py-1 rounded-full text-[12px] xl:text-[12.5px] font-bold text-[#1D4ED8] bg-white border border-[#1D4ED8] hover:bg-blue-50 transition-all hover:-translate-y-0.5 inline-flex items-center whitespace-nowrap"
                  >
                    Download
                  </Link>
                  <Link
                    to="/dashboard"
                    title="User Dashboard"
                    aria-label="User Dashboard"
                    className="px-2 xl:px-2.5 py-1 rounded-full text-[12px] xl:text-[12.5px] font-bold text-[#1D4ED8] bg-blue-50 hover:bg-blue-100 transition-all hover:-translate-y-0.5 flex items-center justify-center shrink-0"
                  >
                    <LayoutDashboard className="h-3.5 w-3.5" />
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="px-2 xl:px-2.5 py-1 rounded-full text-[12px] xl:text-[12.5px] font-semibold text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap"
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/download"
                    className="px-2.5 xl:px-3 py-1 rounded-full text-[12px] xl:text-[12.5px] font-bold text-[#1D4ED8] bg-white border border-[#1D4ED8] hover:bg-blue-50 transition-all hover:-translate-y-0.5 inline-flex items-center whitespace-nowrap"
                  >
                    Download
                  </Link>
                  <button
                    onClick={onOpenLogin}
                    className="px-2 xl:px-2.5 py-1 rounded-full text-[12px] xl:text-[12.5px] font-semibold text-gray-700 hover:text-[#1D4ED8] transition-colors whitespace-nowrap"
                  >
                    Sign In
                  </button>
                </>
              )}
            </div>

            {/* Mobile hamburger menu */}
            <div className="flex lg:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-lg text-blue-600 hover:bg-blue-50 focus:outline-none"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-100 bg-slate-50">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                  isActive('/') ? 'text-blue-600 bg-blue-50' : 'text-slate-800 hover:bg-slate-100'
                }`}
              >
                Home
              </Link>
              <Link
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                  isActive('/about') ? 'text-blue-600 bg-blue-50' : 'text-slate-800 hover:bg-slate-100'
                }`}
              >
                About
              </Link>
              <Link
                to="/features"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                  isActive('/features') ? 'text-blue-600 bg-blue-50' : 'text-slate-800 hover:bg-slate-100'
                }`}
              >
                Features
              </Link>
              <Link
                to="/pricing"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                  isActive('/pricing') ? 'text-blue-600 bg-blue-50' : 'text-slate-800 hover:bg-slate-100'
                }`}
              >
                Pricing
              </Link>
              <Link
                to="/download"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                  isActive('/download') ? 'text-blue-600 bg-blue-50' : 'text-slate-800 hover:bg-slate-100'
                }`}
              >
                Download
              </Link>

              <div className="pt-2 pb-1 border-t border-slate-200">
                <div className="px-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Resources</div>
                <Link
                  to="/blog"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-6 py-2 rounded-xl text-base font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Blog
                </Link>
                <Link
                  to="/newsletter"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-6 py-2 rounded-xl text-base font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Newsletter
                </Link>
                <Link
                  to="/press-release"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-6 py-2 rounded-xl text-base font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Press Release
                </Link>
                <Link
                  to="/security"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-6 py-2 rounded-xl text-base font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Security
                </Link>
              </div>

              <div className="pt-3 pb-2 border-t border-slate-200 space-y-2">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onOpenContact();
                  }}
                  className="w-full text-left px-3 py-2.5 rounded-xl text-base font-semibold text-slate-800 hover:bg-slate-100"
                >
                  Contact Us
                </button>

                {isAuthenticated ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-center px-4 py-2.5 rounded-xl text-base font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md"
                    >
                      Dashboard
                    </Link>
                    <div className="space-y-2 border border-slate-200/80 rounded-xl p-3 bg-white">
                      <div className="flex items-center justify-between px-1">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Try Now</div>
                      </div>
                      <div className="text-xs text-slate-500 px-1 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                        <span>Detected: {detectedEnv.detail || detectedEnv.name}</span>
                      </div>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleDownloadBuild(primaryBuild);
                        }}
                        className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors flex items-center justify-between shadow-sm"
                      >
                        <div className="flex items-center gap-2">
                          {renderOsIcon(detectedEnv.os, "w-4 h-4 text-white")}
                          <span>Download for {detectedEnv.name}</span>
                        </div>
                        <span className="text-xs font-medium opacity-90">{primaryBuild.format}</span>
                      </button>
                      {secondaryBuilds.map((b) => {
                        const isComingSoon = b.status === 'coming_soon';
                        return (
                          <button
                            key={b.id || b.name}
                            disabled={isComingSoon}
                            onClick={() => {
                              if (isComingSoon) return;
                              setMobileMenuOpen(false);
                              handleDownloadBuild(b);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition-colors border border-slate-100 flex items-center justify-between ${
                              isComingSoon
                                ? 'opacity-60 cursor-not-allowed bg-slate-50 text-slate-400'
                                : 'text-slate-700 hover:bg-blue-50/50 hover:text-blue-600'
                            }`}
                          >
                            <span>{b.name} ({b.format})</span>
                            {isComingSoon && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">Coming Soon</span>
                            )}
                          </button>
                        );
                      })}

                      <Link
                        to="/download"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block w-full text-left px-3 py-2 text-xs font-bold text-blue-600 hover:bg-blue-50 transition-colors border-t border-slate-100"
                      >
                        All Platforms &amp; Builds (Win, Mac, Linux) →
                      </Link>
                    </div>
                    <Link
                      to="/download"
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full text-center px-4 py-2.5 rounded-xl text-base font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg block"
                    >
                      ⬇ Download App
                    </Link>
                    <a
                      href={CANDOR_WEB_IDE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full text-center px-4 py-2.5 rounded-xl text-base font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 shadow-sm flex items-center justify-center gap-2"
                    >
                      <Globe className="w-5 h-5 text-blue-600" />
                      <span>Candor Web IDE</span>
                    </a>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleLogout();
                      }}
                      className="w-full text-center px-4 py-2 rounded-xl text-base font-medium text-slate-500 hover:text-slate-800"
                    >
                      Log Out
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col gap-2 pt-1">
                    <a
                      href={CANDOR_WEB_IDE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full text-center px-4 py-2.5 rounded-xl text-base font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md flex items-center justify-center gap-2"
                    >
                      <Globe className="w-5 h-5 text-white" />
                      <span>Candor Web IDE</span>
                    </a>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        onOpenLogin();
                      }}
                      className="block w-full text-center px-4 py-2.5 rounded-xl text-base font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200"
                    >
                      Login
                    </button>
                    <div className="space-y-2 border border-slate-200/80 rounded-xl p-3 bg-white">
                      <div className="flex items-center justify-between px-1">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Try Now Candor 2.0</div>
                      </div>
                      <div className="text-xs text-slate-500 px-1 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                        <span>Detected: {detectedEnv.detail || detectedEnv.name}</span>
                      </div>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleDownloadBuild(primaryBuild);
                        }}
                        className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors flex items-center justify-between shadow-sm"
                      >
                        <div className="flex items-center gap-2">
                          {renderOsIcon(detectedEnv.os, "w-4 h-4 text-white")}
                          <span>Download for {detectedEnv.name}</span>
                        </div>
                        <span className="text-xs font-medium opacity-90">{primaryBuild.format}</span>
                      </button>
                      {secondaryBuilds.map((b) => {
                        const isComingSoon = b.status === 'coming_soon';
                        return (
                          <button
                            key={b.id || b.name}
                            disabled={isComingSoon}
                            onClick={() => {
                              if (isComingSoon) return;
                              setMobileMenuOpen(false);
                              handleDownloadBuild(b);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition-colors border border-slate-100 flex items-center justify-between ${
                              isComingSoon
                                ? 'opacity-60 cursor-not-allowed bg-slate-50 text-slate-400'
                                : 'text-slate-700 hover:bg-blue-50/50 hover:text-blue-600'
                            }`}
                          >
                            <span>{b.name} ({b.format})</span>
                            {isComingSoon && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">Coming Soon</span>
                            )}
                          </button>
                        );
                      })}

                      <Link
                        to="/download"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block w-full text-left px-3 py-2 text-xs font-bold text-blue-600 hover:bg-blue-50 transition-colors border-t border-slate-100"
                      >
                        All Platforms &amp; Builds (Win, Mac, Linux) →
                      </Link>
                    </div>
                    <Link
                      to="/download"
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full text-center px-4 py-2.5 rounded-xl text-base font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg block"
                    >
                      ⬇ Download App
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
